/**
 * 环境检测和域名验证
 */
const isValidOrigin = (origin) => {
  if (!origin) return false;

  // 允许的域名列表（与 u-taxes 项目保持一致）
  const allowedOrigins = [
    // 开发环境
    'http://localhost:3001',
    'http://localhost:3002',  // u-taxes 实际运行端口
    'http://localhost:3000',
    'http://127.0.0.1:3001',
    'http://127.0.0.1:3002',  // u-taxes 实际运行端口
    'http://192.168.31.37:3001',
    'http://192.168.31.37:3002',  // u-taxes 实际运行端口
    'http://192.168.31.245:3001',
    'http://192.168.31.245:3002', // u-taxes 实际运行端口

    // 测试环境
    'http://192.168.31.245:3001',
    'http://192.168.31.245:3002',
    'http://192.168.31.37:3001',
    'http://192.168.31.37:3002',
    'https://test.utaxes.com',
    'https://test-app.utaxes.com',

    // 生产环境
    'https://www.utaxes.com',
    'https://utaxes.com',
    'https://app.utaxes.com',
    'https://u.com',
    'https://www.u.com'
  ];

  // 精确匹配
  if (allowedOrigins.includes(origin)) {
    return true;
  }

  // 域名匹配
  try {
    const originUrl = new URL(origin);
    const allowedDomains = ['localhost', '127.0.0.1', '192.168.31.37', '192.168.31.245', 'utaxes.com', 'u.com'];
    return allowedDomains.some(domain =>
      originUrl.hostname === domain ||
      originUrl.hostname.endsWith(`.${domain}`)
    );
  } catch (error) {
    console.error('Invalid origin URL:', origin, error);
    return false;
  }
};

/**
 * 验证 token 格式是否安全
 */
const isValidToken = (token) => {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // 基础长度检查（JWT token 通常在 100-2048 字符之间）
  if (token.length < 20 || token.length > 2048) {
    return false;
  }

  // 安全字符检查，防止 XSS 和代码注入
  const dangerousChars = ['<', '>', '"', "'", 'javascript:', 'data:', 'vbscript:', 'onload=', 'onerror='];
  if (dangerousChars.some(char => token.toLowerCase().includes(char.toLowerCase()))) {
    return false;
  }

  // 检查是否包含 HTML 标签
  if (/<[^>]*>/g.test(token)) {
    return false;
  }

  // 允许的字符：字母、数字、常见的 token 字符
  // JWT token 通常包含 A-Z, a-z, 0-9, +, /, =, -, _, .
  const validTokenPattern = /^[A-Za-z0-9+/=._-]+$/;
  return validTokenPattern.test(token);
};

// 导出处理函数以保持兼容性
export const handleFlutterMessage = (event) => {
  try {
    const message = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    return window.receiveDataFromFlutter(message);
  } catch (error) {
    return false;
  }
};

// 新增：处理来自 u-taxes 的消息
export const handleUTaxesMessage = (event) => {
  try {
    // 验证来源
    if (!isValidOrigin(event.origin)) {
      console.warn('拒绝来自未授权域名的消息:', event.origin);
      return false;
    }

    const message = event.data;

    // 处理 AUTH_TOKEN 消息
    if (message.type === 'AUTH_TOKEN' && message.token) {
      if (isValidToken(message.token)) {
        localStorage.setItem('firmServiceToken', message.token);
        console.log('成功接收来自 u-taxes 的 token');

        // 通知父窗口登录成功
        if (window.parent !== window) {
          window.parent.postMessage({
            type: 'LOGIN_SUCCESS',
            source: 'app-activity'
          }, event.origin);
        }

        return true;
      } else {
        console.error('收到无效的 token 格式');
        return false;
      }
    }

    return false;
  } catch (error) {
    console.error('处理 u-taxes 消息出错:', error);
    return false;
  }
};

// 全局接收函数（扩展以支持多种来源）
window.receiveDataFromFlutter = (data) => {
  try {
    // 如果传入的是字符串，需要解析
    const message = typeof data === 'string' ? JSON.parse(data) : data;

    // 原有的 Flutter 消息处理
    if (message.type === 'init' && message.data?.token) {
      if (isValidToken(message.data.token)) {
        localStorage.setItem('firmServiceToken', message.data.token);
        console.log('成功接收来自 Flutter 的 token');
        return true;
      } else {
        console.error('收到无效的 Flutter token 格式');
        return false;
      }
    }

    return false;
  } catch (error) {
    // console.error('处理Flutter数据出错:', error);
    return false;
  }
};

// 新增：检查 URL 参数中的 token
export const checkUrlToken = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('token');

    if (urlToken && isValidToken(urlToken)) {
      localStorage.setItem('firmServiceToken', urlToken);
      console.log('成功从 URL 参数获取 token');

      // 清理 URL 参数
      const url = new URL(window.location);
      url.searchParams.delete('token');
      window.history.replaceState({}, '', url);

      return true;
    }

    return false;
  } catch (error) {
    console.error('检查 URL token 出错:', error);
    return false;
  }
};

// 新增：向父窗口请求 token
export const requestTokenFromParent = () => {
  try {
    if (window.parent !== window) {
      window.parent.postMessage({
        type: 'REQUEST_TOKEN',
        source: 'app-activity'
      }, '*');
      console.log('已向父窗口请求 token');
      return true;
    }
    return false;
  } catch (error) {
    console.error('请求父窗口 token 出错:', error);
    return false;
  }
};

// 可选：添加一个就绪通知函数
window.notifyFlutterReady = () => {
  try {
    // 检查 Flutter WebView 是否可用
    if (window.flutterApp) {
      window.flutterApp.callHandler('webviewReady');
    }
  } catch (error) {
    // console.error('通知Flutter就绪状态失败:', error);
  }
}; 